import type { Landmark } from '../../../data/types';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import type { LandmarkContext } from './landmarkFeature';
import { masonryStyles, type Materials } from './landmarkMaterials';
import { SURFACE } from '../../mesh/attributes';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import { buildStadhuis } from './stadhuis/stadhuis';
import { buildOudeKerk } from './oudeKerk/oudeKerk';
import { buildNieuweKerk } from './nieuweKerk/nieuweKerk';
import { buildSintOlofskapel } from './sintOlofskapel';
import { buildHeiligeStede, buildBegijnhofkapel } from './paintedChapels';
import { buildLeprozenhuis } from './leprozenhuis';
import { buildSintPietersgasthuis } from './sintPietersgasthuis';
import { buildDoelenHall } from './doelenHalls';
import { buildPaulusbroedersConvent } from './paulusbroedersConvent';
import { buildScafferij } from './scafferij';
import { buildLastageGardenShed } from './lastageGardenShed';
import { buildOnzeLieveVrouwekapel } from './onzeLieveVrouwekapel';
import { buildAgnietenkapel } from './agnietenkapel';
import { buildAgnietenConvent } from './agnietenConvent';
import { buildBethanienConvent } from './bethanienConvent';
import { buildMinderbroeders } from './minderbroeders';
import { buildCellezusters } from './cellezusters';
import { buildPrinsenhofConvent } from './prinsenhofConvents';
import { buildUrsulaConvent } from './ursulaConvent';
import { buildOudeNonnen } from './oudeNonnen';
import { buildNieuweNonnen } from './nieuweNonnen';
import { buildSintMaria } from './sintMaria';
import { buildCellebroeders } from './cellebroeders';
import { buildMagdalenaBarbara } from './magdalenaBarbara';
import { buildSintClara } from './sintClara';
import { buildSintMargriet } from './sintMargriet';
import { buildSintGeertruid } from './sintGeertruid';
import { buildSacramentsHall } from './sacramentsHall';
import { buildEersteWeeshuis } from './eersteWeeshuis';
import { buildKartuizers } from './kartuizers';
import { buildLucienerf } from './lucienerf';
import { buildSintLucien } from './sintLucien';
import { buildSintPieterskapel } from './sintPieterskapel';
import { buildHeiligewegClarissen } from './heiligewegClarissen';

/**
 * The big buildings drawn from the sources part by part (docs/plans/landmarks.md); every other
 * landmark is raised by the generic builder of its kind.
 */
export function dispatchBespoke(
  builder: MeshBuilder,
  landmark: Landmark,
  m: Materials,
  ids: { edgeId: number; seed: number },
  context: LandmarkContext & { readonly tracery?: TracerySink }
): boolean {
  const tracery = context.tracery ?? NO_TRACERY;
  const conventColors = {
    wall: m.brick,
    roof: m.churchSlate,
    dark: m.dark,
    glass: m.glass,
    dressing: m.dressing,
    tile: m.tile,
    ground: m.earth,
    garden: m.garth,
  };
  switch (landmark.id) {
    case 'landmark-kartuizerklooster':
      buildKartuizers(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-clarissen':
      buildHeiligewegClarissen(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-lucienerf-carpenter':
      buildLucienerf(builder, landmark, { ...conventColors, timber: m.timber }, ids, context);
      return true;
    case 'landmark-eerste-weeshuis':
      buildEersteWeeshuis(builder, landmark, conventColors, ids);
      return true;
    case 'landmark-kalverstraat-painted-hall':
      buildSacramentsHall(builder, landmark, conventColors, ids);
      return true;
    case 'landmark-sint-pieterskapel':
      buildSintPieterskapel(builder, landmark, conventColors, ids, tracery);
      return true;
    case 'landmark-sint-lucien':
      buildSintLucien(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-geertruid':
      buildSintGeertruid(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-margriet':
      buildSintMargriet(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-clara':
      buildSintClara(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-maria-magdalena':
    case 'landmark-sint-barbara':
      buildMagdalenaBarbara(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-cellebroeders':
      buildCellebroeders(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-maria':
      buildSintMaria(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-nieuwe-nonnen':
      buildNieuweNonnen(
        builder,
        landmark,
        {
          wall: m.brick,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          tile: m.tile,
          ground: m.earth,
          garden: m.garth,
          timber: m.timber,
          foliage: m.treeFoliage,
        },
        ids,
        tracery,
        context
      );
      return true;
    case 'landmark-oude-nonnen':
      buildOudeNonnen(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-ursula':
      buildUrsulaConvent(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-sint-cecilia':
    case 'landmark-sint-catharina':
      buildPrinsenhofConvent(builder, landmark, conventColors, ids, tracery, context);
      return true;
    case 'landmark-cellezusters':
      buildCellezusters(builder, landmark, conventColors, ids, tracery);
      return true;
    case 'landmark-minderbroeders':
      buildMinderbroeders(
        builder,
        landmark,
        {
          wall: m.brick,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          ground: m.earth,
          garden: m.garth,
          timber: m.timber,
          foliage: m.treeFoliage,
        },
        ids,
        tracery,
        context
      );
      return true;
    case 'landmark-bethanien':
      buildBethanienConvent(builder, landmark, conventColors, ids, tracery);
      return true;
    case 'landmark-agnieten-convent':
      buildAgnietenConvent(
        builder,
        landmark,
        {
          wall: m.brick,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          ground: m.earth,
          garden: m.garth,
        },
        ids
      );
      return true;
    case 'landmark-agnietenkapel':
      buildAgnietenkapel(
        builder,
        landmark,
        { wall: m.brick, roof: m.churchSlate, dark: m.dark, glass: m.glass, dressing: m.dressing },
        ids,
        tracery
      );
      return true;
    case 'landmark-onze-lieve-vrouwekapel':
      buildOnzeLieveVrouwekapel(
        builder,
        landmark,
        { wall: m.brick, roof: m.churchSlate, dark: m.dark, glass: m.glass, dressing: m.dressing },
        ids,
        tracery
      );
      return true;
    case 'landmark-lastage-garden-shed':
      buildLastageGardenShed(
        builder,
        landmark,
        { timber: m.timber, roof: m.reed, dark: m.dark },
        ids
      );
      return true;
    case 'landmark-scafferij-baerdsehuys': {
      const styles = masonryStyles(
        { color: m.stone, surface: SURFACE.stone },
        { color: m.tile, surface: SURFACE.roofTile },
        m,
        ids
      );
      buildScafferij(builder, landmark, {
        ...styles,
        opening: { ...styles.opening, color: m.glass },
        glazing: { reveal: { ...styles.wall, color: m.dressing } },
        timber: { ...styles.wall, color: m.timber, surface: SURFACE.wood },
      });
      return true;
    }
    case 'landmark-paulusbroederskapel':
      buildPaulusbroedersConvent(
        builder,
        landmark,
        {
          wall: m.brick,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          timber: m.timber,
          foliage: m.treeFoliage,
        },
        ids,
        tracery,
        context
      );
      return true;
    case 'landmark-handboogdoelen':
    case 'landmark-voetboogdoelen':
      buildDoelenHall(
        builder,
        landmark,
        {
          wall: m.stone,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          tile: m.tile,
        },
        ids,
        tracery
      );
      return true;
    case 'landmark-sint-pietersgasthuis':
      buildSintPietersgasthuis(
        builder,
        landmark,
        {
          wall: m.brick,
          roof: m.churchSlate,
          glass: m.glass,
          trim: m.dressing,
          ground: m.earth,
          timber: m.timber,
          cloth: m.plaster,
        },
        ids
      );
      return true;
    case 'landmark-leprozenhuis':
      buildLeprozenhuis(
        builder,
        landmark,
        {
          wall: m.stone,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          tile: m.tile,
          timber: m.timber,
          garden: m.garth,
        },
        ids,
        tracery
      );
      return true;
    case 'landmark-heilige-stede':
    case 'landmark-begijnhofkapel': {
      const colors = {
        wall: m.stone,
        roof: m.churchSlate,
        dark: m.dark,
        glass: m.glass,
        dressing: m.dressing,
      };
      const build =
        landmark.id === 'landmark-heilige-stede' ? buildHeiligeStede : buildBegijnhofkapel;
      build(builder, landmark, colors, ids, tracery);
      return true;
    }
    case 'landmark-sint-olofskapel':
      buildSintOlofskapel(
        builder,
        landmark,
        {
          wall: m.stone,
          roof: m.churchSlate,
          dark: m.dark,
          glass: m.glass,
          dressing: m.dressing,
          timber: m.timber,
        },
        ids,
        tracery
      );
      return true;
    case 'landmark-oude-stadhuis': {
      const colors = {
        stone: m.stone,
        brick: m.hallBrick,
        slate: m.churchSlate,
        dark: m.dark,
        glass: m.glass,
        leaf: m.leaf,
      };
      buildStadhuis(builder, landmark, colors, context, ids);
      return true;
    }
    case 'landmark-oude-kerk': {
      const colors = {
        stone: m.kerkWall,
        slate: m.churchSlate,
        lead: m.lead,
        timber: m.timber,
        dark: m.dark,
        glass: m.glass,
        dressing: m.dressing,
      };
      buildOudeKerk(builder, landmark, colors, ids, tracery);
      return true;
    }
    case 'landmark-nieuwe-kerk': {
      const colors = {
        wall: m.nieuweKerkWall,
        slate: m.churchSlate,
        lead: m.lead,
        dark: m.dark,
        glass: m.glass,
        dressing: m.dressing,
      };
      buildNieuweKerk(builder, landmark, colors, ids, tracery);
      return true;
    }
    default:
      return false;
  }
}
